import { KdsLink, KdsText } from "@kazaar-platform/design-system";
import { RiLinksLine } from "@remixicon/react";
import { T, useTranslate } from "@tolgee/react";
import { type ReactNode, useMemo } from "react";
import { OrderItemDetailsOrderLogReadDtoActionTypeEnum } from "@/config/apiClient";
import { type TransitionData } from "@/types/transitionData";
import { OrderItemPageActivityLogCancellableStep } from "../OrderItemPageActivityLogCancellableStep/OrderItemPageActivityLogCancellableStep";
import { type OrderItemPageActivityLogQuoteProps } from "./OrderItemPageActivityLogQuote.types";

export const OrderItemPageActivityLogQuote = ({
  associatedOrderItemsLength,
  file,
  refusalReason,
  orderItemLog,
}: OrderItemPageActivityLogQuoteProps) => {
  const { t } = useTranslate();

  const transitionData = orderItemLog.transitionData as TransitionData;

  // Define infoIconLabel
  const infoIconLabel: string = useMemo(() => {
    if (
      orderItemLog.actionType ===
      OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteRefused
    ) {
      return t(
        "order.myOrdersItemDetails.activityFeed.action.infoIcon.refused"
      );
    }

    if (
      orderItemLog.actionType ===
      OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteValidated
    ) {
      return t(
        "order.myOrdersItemDetails.activityFeed.action.infoIcon.validated"
      );
    }

    return t(
      "order.myOrdersItemDetails.activityFeed.action.infoIcon.proposed",
      { count: associatedOrderItemsLength }
    );
  }, [orderItemLog.actionType, t]);

  // Define customMessage
  const customMessage: ReactNode | null = useMemo(() => {
    if (
      orderItemLog.actionType ===
      OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteCancelledPreviouslyRefused
    ) {
      return (
        <div className="flex items-center gap-sp-xs2">
          <KdsText>
            {t(
              "order.myOrdersItemDetails.activityFeed.action.quotes.cancelledPreviouslyRefused"
            )}
          </KdsText>
          <KdsLink href={file?.url} target="_blank" size="sm">
            {file?.name}
          </KdsLink>
          <KdsText>
            {t(
              "order.myOrdersItemDetails.activityFeed.action.quotes.hasBeenSuspended"
            )}
          </KdsText>
        </div>
      );
    }

    if (
      orderItemLog.actionType ===
      OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteCancelledPreviouslyValidated
    ) {
      const relatedOrderItemsCount =
        transitionData?.relatedOrderItemsIds?.length || 0;

      return (
        <div className="flex flex-col">
          <div className="flex items-center gap-sp-xs2">
            <KdsText>
              {t(
                "order.myOrdersItemDetails.activityFeed.action.quotes.cancelledPreviouslyValidated"
              )}
            </KdsText>
            <KdsLink href={file?.url} target="_blank" size="sm">
              {file?.name}
            </KdsLink>
            <KdsText>
              {t(
                "order.myOrdersItemDetails.activityFeed.action.quotes.hasBeenSuspended"
              )}
            </KdsText>
          </div>

          {relatedOrderItemsCount > 1 && (
            <div className="flex items-center gap-1">
              <RiLinksLine className="size-si-xs2" />
              <T
                keyName="order.myOrdersItemDetails.activityFeed.action.quotes.multipleSuffix"
                params={{
                  count: relatedOrderItemsCount - 1,
                  b: <KdsText size="subtitle-sm" />,
                }}
              />
              {t(
                "order.myOrdersItemDetails.activityFeed.action.quotes.hasBeenSuspended"
              )}{" "}
            </div>
          )}
        </div>
      );
    }

    if (
      orderItemLog.actionType ===
      OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteCancelled
    ) {
      return (
        <div className="flex items-baseline gap-sp-xs2">
          <T
            keyName="order.myOrdersItemDetails.activityFeed.action.quotes.cancelledWorkflow"
            params={{
              productName: transitionData?.orderItemName,
              b: <KdsText size="subtitle-sm" />,
            }}
          />
        </div>
      );
    }

    return null;
  }, [
    orderItemLog.actionType,
    t,
    file?.name,
    file?.url,
    transitionData?.orderItemName,
    transitionData?.relatedOrderItemsIds?.length,
  ]);

  return (
    <OrderItemPageActivityLogCancellableStep
      orderItemLog={orderItemLog}
      associatedOrderItemsLength={associatedOrderItemsLength}
      refusalReason={refusalReason}
      customMessage={customMessage}
      introText={t(
        "order.myOrdersItemDetails.activityFeed.action.quotes.intro"
      )}
      suffixText={t(
        "order.myOrdersItemDetails.activityFeed.action.quotes.suffix",
        { count: associatedOrderItemsLength }
      )}
      file={file}
      infoIconLabel={infoIconLabel}
    />
  );
};
